import React, { useState } from 'react';
import PaymentSetup from './PaymentSetup';
import { useCardFeature } from '../../hooks/useCardFeature';
import { stripeApi } from '../../firebase/functionsService';
import { COLORS, SHADOWS } from '../../styles/tokens';

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

function PaymentCardSectionInner({ userDoc, currentUser, onRefresh }) {
  const hasCard = !!userDoc?.stripeDefaultPaymentMethod;
  const [open, setOpen]         = useState(false);
  const [removing, setRemoving] = useState(false);
  const [mode, setMode]         = useState('view');

  const cardBrand = capitalize(userDoc?.cardBrand ?? '');
  const cardLast4 = userDoc?.cardLast4;
  const expMonth  = String(userDoc?.cardExpMonth ?? '').padStart(2, '0');
  const expYear   = String(userDoc?.cardExpYear  ?? '').slice(-2);
  const expStr    = cardLast4 ? `${expMonth}/${expYear}` : null;

  async function handleRemove() {
    if (!window.confirm('Fjern betalingskort?')) return;
    setRemoving(true);
    try {
      await stripeApi.removeDefaultPaymentMethod();
      await onRefresh();
      setOpen(false);
      setMode('view');
    } catch {
      window.alert('Kunne ikke fjerne kortet. Prøv igjen.');
    } finally {
      setRemoving(false);
    }
  }

  async function handleCardSaved() {
    await onRefresh();
    setOpen(false);
    setMode('view');
  }

  // Hide entirely if card is saved and user hasn't opened it
  if (hasCard && !open) {
    return (
      <button
        style={pcStyles.closedPill}
        onClick={() => setOpen(true)}
      >
        <span style={{ fontSize: 15 }}>💳</span>
        <span style={pcStyles.closedPillText}>{cardBrand} **** {cardLast4}</span>
        <span style={pcStyles.savedBadge}>✓ Lagret</span>
        <span style={{ color: COLORS.textLight, fontSize: 14 }}>›</span>
      </button>
    );
  }

  return (
    <div style={pcStyles.wrapper}>
      <button style={pcStyles.header} onClick={() => { setOpen(o => !o); setMode('view'); }}>
        <span style={{ fontSize: 18 }}>💳</span>
        <div style={{ flex: 1, textAlign: 'left' }}>
          {hasCard && cardLast4 ? (
            <>
              <div style={pcStyles.headerTitle}>{cardBrand} **** {cardLast4}</div>
              <div style={pcStyles.headerSub}>Utløper {expStr}</div>
            </>
          ) : (
            <>
              <div style={pcStyles.headerTitle}>Legg til betalingskort</div>
              <div style={pcStyles.headerSub}>Du betaler etter levering</div>
            </>
          )}
        </div>
        {hasCard && <span style={pcStyles.savedBadge}>✓ Lagret</span>}
        <span style={{ color: COLORS.textLight, fontSize: 16, transform: open ? 'rotate(90deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}>‹</span>
      </button>

      {open && (
        <div style={pcStyles.body}>
          {hasCard && mode === 'view' ? (
            <div style={pcStyles.cardInfo}>
              <div style={pcStyles.cardDisplay}>
                <span style={{ fontSize: 28 }}>💳</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{cardBrand} **** {cardLast4}</div>
                  <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 2 }}>Utløper {expStr}</div>
                </div>
              </div>
              <div style={pcStyles.cardActions}>
                <button className="tippi-btn-secondary" style={pcStyles.changeBtn} onClick={() => setMode('edit')}>Endre kort</button>
                <button style={pcStyles.removeBtn} onClick={handleRemove} disabled={removing}>
                  {removing ? 'Fjerner…' : 'Fjern kort'}
                </button>
              </div>
            </div>
          ) : (
            <PaymentSetup onSuccess={handleCardSaved} />
          )}
        </div>
      )}
    </div>
  );
}



const pcStyles = {
  closedPill:  { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: COLORS.white, borderRadius: 10, border: 'none', cursor: 'pointer', boxShadow: SHADOWS.sm, marginBottom: 24, width: '100%', textAlign: 'left' },
  closedPillText: { fontSize: 14, fontWeight: 600, color: COLORS.primary, flex: 1 },
  wrapper:     { background: COLORS.white, borderRadius: 10, boxShadow: SHADOWS.sm, marginBottom: 24, overflow: 'hidden' },
  header:      { width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
  headerTitle: { fontWeight: 700, fontSize: 14, color: COLORS.primary },
  headerSub:   { fontSize: 12, color: COLORS.textLight, marginTop: 2 },
  savedBadge:  { background: COLORS.successBg, color: COLORS.successText, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  body:        { padding: '0 18px 18px', borderTop: `1px solid ${COLORS.border}` },
  cardInfo:    { paddingTop: 14 },
  cardDisplay: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: COLORS.surfaceHover, borderRadius: 8, marginBottom: 12 },
  cardActions: { display: 'flex', gap: 10 },
  changeBtn:   { flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, fontWeight: 600, fontSize: 14, cursor: 'pointer', color: COLORS.primary },
  removeBtn:   { flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: COLORS.dangerBg, fontWeight: 600, fontSize: 14, cursor: 'pointer', color: COLORS.dangerText },
};


export default function PaymentCardSection(props) {
  const { enabled } = useCardFeature();
  return enabled ? <PaymentCardSectionInner {...props} /> : null;
}
